import { OnboardingDialogAction } from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { Features } from '@app/enums';
import { PlanType } from '@app/reducers/account/account-subscription.model';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

export enum ManagerOnboardingChecklistStep {
  COMPLETE_SIGNUP = 'COMPLETE_SIGNUP',
  SETUP_ORGANIZATION_SETUP = 'SETUP_ORGANIZATION_SETUP',
  ADD_EMPLOYEES = 'ADD_EMPLOYEES',
  SCHEDULE_EMPLOYEE = 'SCHEDULE_EMPLOYEE',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
}

// TODO when moving these models to shiftbase/models packaging,
// rename these consts/enum to be more generic and not associated to only hospitality
// SHB-11513
export enum SmbHospitalityOnboardingChecklistStep {
  SCHEDULE_SHIFT = 'SCHEDULE_SHIFT',
  OPTIMIZED_SCHEDULE = 'OPTIMIZED_SCHEDULE',
  ABSENCE_MANAGEMENT = 'ABSENCE_MANAGEMENT',
  TIME_TRACKING = 'TIME_TRACKING',
  PAYROLL = 'PAYROLL',
  MOBILE_APP = 'MOBILE_APP',
  INVITE_TEAM = 'INVITE_TEAM',
}

export enum ActivationOnboardingStep {
  TARGET_SETTING = 'TARGET_SETTING',
}

export enum InternalOnboardingStep {
  // INVITE_TEAM
  CELEBRATION_INVITED = 'CELEBRATION_INVITED',
  CELEBRATION_SKIPPED = 'CELEBRATION_SKIPPED',
  // TIME_TRACKING
  TIME_TRACKING_DEMO = 'TIME_TRACKING_DEMO',
  TIME_TRACKING_SUMMARY = 'TIME_TRACKING_SUMMARY',
  // OPTIMIZED_SCHEDULE
  OPTIMIZED_SCHEDULE_DEMO = 'OPTIMIZED_SCHEDULE_DEMO',
  OPTIMIZED_SCHEDULE_SUMMARY = 'OPTIMIZED_SCHEDULE_SUMMARY',
  // ABSENCE_MANAGEMENT
  ABSENCE_MANAGEMENT_DEMO = 'ABSENCE_MANAGEMENT_DEMO',
  ABSENCE_MANAGEMENT_SETUP = 'ABSENCE_MANAGEMENT_SETUP',
}

export const smbHospitalityOnboardingChecklistStepConfiguration: Partial<
  Record<OnboardingChecklistStep, OnboardingChecklistStepConfiguration>
> = {
  [SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT]: {
    requiredRoute: 'schedule/employee/week',
  },
  [SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT]: {
    minimumPlanRequired: PlanType.BASIC,
  },
  [SmbHospitalityOnboardingChecklistStep.TIME_TRACKING]: {
    minimumPlanRequired: PlanType.BASIC,
  },
  [SmbHospitalityOnboardingChecklistStep.PAYROLL]: {
    minimumPlanRequired: PlanType.BASIC,
    featureFlag: Features.TMP_SELF_ONBOARDING_PAYROLL_CHAPTER,
    excludedCountries: ['DE'],
  },
  [SmbHospitalityOnboardingChecklistStep.INVITE_TEAM]: {
    // Overwrite the priority CTA
    customNextStep: {
      primary: {
        step: InternalOnboardingStep.CELEBRATION_INVITED,
      },
      secondary: {
        step: InternalOnboardingStep.CELEBRATION_SKIPPED,
      },
    },
  },
};

export const onboardingChaptersCTAByPriority: Array<OnboardingChaptersCTA> = [
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT,
      label: _('Continue with adding your first Shift'),
    },
  },
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.OPTIMIZED_SCHEDULE,
      label: _('Continue with Optimised Scheduling'),
    },
  },
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.TIME_TRACKING,
      label: _('Continue with Time Tracking'),
    },
  },
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT,
      label: _('Continue with Absence Management'),
    },
  },
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.PAYROLL,
      label: _('Continue with Payroll'),
    },
  },
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.MOBILE_APP,
      label: _('Manage your business on the go'),
    },
  },
  {
    primary: {
      step: SmbHospitalityOnboardingChecklistStep.INVITE_TEAM,
      label: _('Continue with Invite Your Employees'),
    },
  },
];

export interface OnboardingChaptersCTA {
  primary: {
    step?: OnboardingStep;
    label?: string;
  };
  secondary?: {
    step?: OnboardingStep;
    label?: string;
  };
}

export type OnboardingStep = SmbHospitalityOnboardingChecklistStep | InternalOnboardingStep | ActivationOnboardingStep;

export type ManagerOnboardingChecklistStepKeys = keyof typeof ManagerOnboardingChecklistStep;
export type SmbHospitalityOnboardingChecklistStepKeys = keyof typeof SmbHospitalityOnboardingChecklistStep;

export type OnboardingChecklistStep = ManagerOnboardingChecklistStepKeys | SmbHospitalityOnboardingChecklistStepKeys;

export type NextOnboardingStepMap = Partial<Record<OnboardingDialogAction, OnboardingStep>>;

export interface OnboardingChecklistStepConfiguration {
  minimumPlanRequired?: PlanType;
  featureFlag?: Features;
  customNextStep?: OnboardingChaptersCTA;
  excludedCountries?: string[];
  requiredRoute?: string;
}
